import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyQuotes } from '../services/api';
import { decodeHtmlEntities } from '../utils/categoryUtils';

interface MyQuote {
  id: number;
  request_id: number;
  price: string;
  message: string;
  warranty_info: string;
  estimated_delivery: string;
  status: string;
  created_at: string;
  request_title: string;
  request_status: string;
}

const MyQuotes: React.FC = () => {
  const [quotes, setQuotes] = useState<MyQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyQuotes();
  }, []);

  const fetchMyQuotes = async () => {
    try {
      const response = await getMyQuotes();
      setQuotes(response.data.quotes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load your quotes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return '#27ae60';
      case 'rejected': return '#e74c3c';
      case 'pending': return '#f39c12';
      default: return '#7f8c8d';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'accepted': return '🏆';
      case 'rejected': return '❌';
      case 'pending': return '⏳';
      default: return '❓';
    }
  };

  if (loading) return <div style={styles.loading}>Loading your quotes...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  const acceptedQuotes = quotes.filter(q => q.status === 'accepted');
  const pendingQuotes = quotes.filter(q => q.status === 'pending');
  const rejectedQuotes = quotes.filter(q => q.status === 'rejected');

  return (
    <div style={styles.container}>
      <h1>💼 My Submitted Quotes</h1>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={{...styles.statCard, borderColor: '#27ae60'}}>
          <h2>{acceptedQuotes.length}</h2>
          <p>🏆 Won</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#f39c12'}}>
          <h2>{pendingQuotes.length}</h2>
          <p>⏳ Pending</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#e74c3c'}}>
          <h2>{rejectedQuotes.length}</h2>
          <p>❌ Lost</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#3498db'}}>
          <h2>{quotes.length > 0 ? Math.round((acceptedQuotes.length / quotes.length) * 100) : 0}%</h2>
          <p>📊 Win Rate</p>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div style={styles.empty}>
          <h3>No quotes submitted yet</h3>
          <p>Browse open requests and submit your competitive quotes!</p>
          <Link to="/requests" style={styles.browseButton}>Browse Requests</Link>
        </div>
      ) : (
        <div style={styles.list}>
          {quotes.map((quote) => (
            <div key={quote.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={{...styles.status, backgroundColor: getStatusColor(quote.status)}}>
                  {getStatusEmoji(quote.status)} {quote.status}
                </span>
                <span style={styles.price}>${quote.price}</span>
              </div>

              <h3 style={styles.title}>{quote.request_title}</h3>
              <p style={styles.message}>{decodeHtmlEntities(quote.message)?.substring(0, 100)}...</p>
              <div style={styles.meta}>
                {quote.warranty_info && <span>🛡️ {decodeHtmlEntities(quote.warranty_info)}</span>}
                {quote.estimated_delivery && <span>🚚 {decodeHtmlEntities(quote.estimated_delivery)}</span>}
              </div>

              <Link to={`/request/${quote.request_id}`} style={styles.viewButton}>
                View Request
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '40px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    textAlign: 'center',
    borderBottom: '4px solid',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
  },
  error: {
    textAlign: 'center',
    padding: '50px',
    color: '#e74c3c',
  },
  empty: {
    textAlign: 'center',
    padding: '50px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  browseButton: {
    display: 'inline-block',
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: '#3498db',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '5px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  card: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  status: {
    padding: '5px 12px',
    borderRadius: '15px',
    color: 'white',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  price: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#27ae60',
  },
  title: {
    marginBottom: '10px',
    color: '#2c3e50',
  },
  message: {
    color: '#7f8c8d',
    marginBottom: '15px',
  },
  meta: {
    display: 'flex',
    gap: '20px',
    marginBottom: '15px',
    color: '#7f8c8d',
    fontSize: '14px',
  },
  viewButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '5px',
  },
};

export default MyQuotes;